/* eslint  no-restricted-syntax: 0 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import translationKeys from 'translations/translationKeys';

// eslint-disable-next-line import/no-extraneous-dependencies
import { printQzTicket, setQzSecurityKeys } from '@dme/ticket-print';
import configs from '../../config';

const apiBaseUrl = configs.BASE_URL_API_PRINT;

const client = axios.create({
  baseURL: apiBaseUrl,
});

const {
  msg23, msg29, msg46, msg116, msg147, msg159, msg220, msg229,
} = translationKeys;

// Load key from file path
async function loadKeyFromPath(filePath) {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to load key from ${filePath}`);
    }
    const keyContent = await response.text();
    return keyContent;
  } catch (error) {
    console.error('Error loading key:', error);
    return '';
  }
}

// Load printer configuration from JSON file
async function loadPrinterConfig() {
  try {
    const configPath = configs.PRINTER_CONFIG;

    if (!configPath) {
      console.warn('Printer config path not specified, using defaults');
      return {
        printerName: 'POS80 Printer',
        cpl: 48,
        count: 1,
        qzPrintingEnabled: false,
      };
    }

    const response = await fetch(`${process.env.PUBLIC_URL}/${configPath}`);

    if (!response.ok) {
      throw new Error(`Failed to load printer config from ${configPath}`);
    }

    const printerConfig = await response.json();

    // Validate and provide defaults
    return {
      printerName: printerConfig.printerName || 'POS80 Printer',
      cpl: parseInt(printerConfig.cpl || '48', 10),
      count: parseInt(printerConfig.count || '1', 10),
      qzPrintingEnabled: printerConfig.qzPrintingEnabled === true,
    };
  } catch (error) {
    console.error('Error loading printer config, using defaults:', error);
    // Fallback to environment variables
    return {
      printerName: 'POS80 Printer',
      cpl: 48,
      count: 1,
      qzPrintingEnabled: false,
    };
  }
}

// Initialize QZ security keys once
let qzKeysInitialized = false;
let qzKeysLoading = false;

async function initializeQzKeys() {
  if (qzKeysInitialized) return;
  if (qzKeysLoading) return; // Prevent multiple simultaneous loads

  qzKeysLoading = true;

  try {
    // Load certificate and private key from public folder
    const certificatePath = configs.QZ_ENC_CERTIFICATE_KEY;
    const privateKeyPath = configs.QZ_ENC_PRIVATE_KEY;

    if (!certificatePath || !privateKeyPath) {
      console.error('QZ key paths not configured');
      qzKeysLoading = false;
      return;
    }

    const [certificate, privateKey] = await Promise.all([
      loadKeyFromPath(`${process.env.PUBLIC_URL}/${certificatePath}`),
      loadKeyFromPath(`${process.env.PUBLIC_URL}/${privateKeyPath}`),
    ]);

    if (certificate && privateKey) {
      setQzSecurityKeys({ certificate, privateKey });
      qzKeysInitialized = true;
      console.log('QZ security keys initialized successfully');
    } else {
      console.error('Failed to load QZ keys from files');
    }
  } catch (error) {
    console.error('Error initializing QZ keys:', error);
  } finally {
    qzKeysLoading = false;
  }
}

export const printTicket = createAsyncThunk(
  'Tickets/print ticket',
  async (ticket, thunkAPI) => {
    // const encodedData = encodeURIComponent(JSON.stringify(ticket));
    // const api = `/print?ticket=${encodedData}`;
    const api = '/print-esc-pos';
    try {
      // const resp = await client.get(api);
      // remove the two last lines that content the flexbet signature
      let lines = [...ticket];
      lines.splice(lines.length - 2, 2);
      lines = lines.map((line) => line.trim());
      const bonusIndex = lines.findIndex((line) => line.includes('Bonus'));
      if (bonusIndex > -1) {
        lines.splice(bonusIndex, 1);
        lines.splice(5, 1, `${lines[5]} - FREE BET`);
      }
      // Load printer configuration from JSON file
      const printerConfig = await loadPrinterConfig();

      if (printerConfig.qzPrintingEnabled) {
        // Initialize QZ keys if not already done
        await initializeQzKeys();

        if (!qzKeysInitialized) {
          return thunkAPI.rejectWithValue({
            success: false,
            message: msg116,
            method: 'qz-tray',
          });
        }

        let logoPath = configs.PRINT_LOGO || null;
        if (!logoPath.includes('http')) {
          logoPath = `${process.env.PUBLIC_URL}/${logoPath}`;
        }

        console.log('Printer configuration loaded:', printerConfig);

        // Print using QZ Tray
        const result = await printQzTicket({
          lines,
          printerName: printerConfig.printerName,
          copies: printerConfig.count,
          cpl: printerConfig.cpl,
          logoPath,
          maxLogoWidth: 384,
          maxLogoHeight: 200,
        });

        if (result.success) {
          return {
            erc: 1,
            message: msg46,
            method: 'qz-tray',
          };
        }

        return thunkAPI.rejectWithValue({
          success: false,
          message: msg159,
          method: 'qz-tray',
        });
      }
      const resp = await client.post(api, { lines }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return {
        ...resp.data,
        method: 'http-client',
      };
    } catch (error) {
      console.error('Print error:', error);

      let errorMessage = msg159; // Default: 'Une erreur est survenue avec l'imprimante'
      const qzEnabled = configs.QZ_PRINTING_ENABLED === 'true';

      if (qzEnabled) {
        // QZ Tray specific errors
        if (error.message) {
          if (error.message.includes('Unable to establish connection')) {
            errorMessage = msg220; // 'L'imprimante n'est pas connectée'
          } else if (error.message.includes('Printer not found')) {
            errorMessage = msg220; // 'L'imprimante n'est pas connectée'
          } else if (error.message.includes('lost connection')) {
            errorMessage = msg23; // 'Erreur de connexion, veuillez contacter le support !'
          } else if (error.message.includes('Certificate')) {
            errorMessage = msg116; // 'Autorisation échouée'
          } else if (error.message.includes('lines must be an array')) {
            errorMessage = msg147; // 'Paramètre manquant!'
          } else if (error.message.includes('printerName is required')) {
            errorMessage = msg147; // 'Paramètre manquant!'
          } else {
            errorMessage = msg159; // 'Une erreur est survenue avec l'imprimante'
          }
        }
      } else if (error.response) {
        // HTTP client specific errors
        if (error.response.status === 404) {
          errorMessage = msg220; // 'L'imprimante n'est pas connectée'
        } else if (error.response.status === 500) {
          errorMessage = msg29; // 'Une erreur est survenue sur le serveur'
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = msg23; // 'Erreur de connexion, veuillez contacter le support !'
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: errorMessage,
        method: qzEnabled ? 'qz-tray' : 'http-client',
      });
    }
  },
);

const initialState = {
  printSuccess: false,
  isPrintLoading: false,
  printError: false,
  unPrintedTicket: false,
  checkedUnPrintedTicket: false,
  printErrorMessage: '',
  printMethod: null, // 'qz-tray' or 'http-client'
};

const printTicketSlice = createSlice({
  name: 'print ticket',
  initialState,
  reducers: {
    refreshPrinting: (state) => ({ ...state, ...initialState }),
    printing: (state) => ({
      ...state, isPrintLoading: true, printError: false, printErrorMessage: '',
    }),
    checkUnprintedTicket: (state) => {
      const unPrintedTicket = localStorage.getItem('currentTicket') !== null;

      return {
        ...state, unPrintedTicket, checkedUnPrintedTicket: true,
      };
    },
    setPrintStatus: (state, { payload }) => {
      if (payload.status) {
        localStorage.removeItem('currentTicket');
      }

      return {
        ...state,
        printError: !payload.status,
        printSuccess: payload.status,
        printErrorMessage: payload.status ? (payload.msg || msg159) : '',
        checkedUnPrintedTicket: false,
        isPrintLoading: false,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(printTicket.pending, (state) => ({
        ...state,
        isPrintLoading: true,
        printError: false,
      }))
      .addCase(printTicket.fulfilled, (state, { payload }) => {
        let printSuccess = false;
        let printError = true;
        let printErrorMessage = '';

        // Handle response based on method
        if (payload?.method === 'qz-tray') {
          // QZ Tray response
          if (Number(payload?.erc) === 1) {
            localStorage.removeItem('currentTicket');
            printSuccess = true;
            printError = false;
          } else {
            printErrorMessage = payload?.message || msg159;
          }
        } else if (payload?.method === 'http-client') {
          // HTTP client response
          if (Number(payload?.erc) === 1) {
            localStorage.removeItem('currentTicket');
            printSuccess = true;
            printError = false;
          } else if (Number(payload?.erc) === 404) {
            printErrorMessage = msg220; // 'L'imprimante n'est pas connectée'
          } else {
            printErrorMessage = payload?.message || msg159;
          }
        } else {
          // Unknown response format
          printErrorMessage = msg229; // 'Une erreur s'est produite pendant le processus'
        }

        return {
          ...state,
          printSuccess,
          isPrintLoading: false,
          printError,
          checkedUnPrintedTicket: false,
          printErrorMessage,
          printMethod: payload?.method || null,
        };
      })

      .addCase(printTicket.rejected, (state, { payload }) => ({
        ...state,
        isPrintLoading: false,
        printSuccess: false,
        printError: true,
        checkedUnPrintedTicket: false,
        printErrorMessage: payload?.message || msg159,
        printMethod: payload?.method || null,
      }));
  },
});

export const {
  refreshPrinting, checkUnprintedTicket,
  setPrintStatus, printing,
} = printTicketSlice.actions;

export default printTicketSlice.reducer;
